/**
 * R7 WS-3 §3 E41–E49 — the identity surface, **through site-admin-api's PROXY paths only**.
 *
 * | # | endpoint | proxy `file:line` | permission · **kind** |
 * |---|---|---|---|
 * | E41 | `GET /v1/admin/users` | `users-proxy.controller.ts:45-46` | `cp:operator:read` · **site** |
 * | E42 | `GET /v1/admin/users/:id/effective-permissions` | same `:61-62` | `cp:operator:read` · **site** |
 * | E43 | `GET /v1/admin/permissions/grants` | `permissions-proxy.controller.ts:170-171` | `cp:permission-grant:read` · **site** |
 * | E44 | `GET /v1/admin/permissions/roles/:slug` | same `:87-88` | `cp:role:read` · **site** |
 * | E45 | `GET /v1/admin/permissions/roles/operators/:operatorId` | same `:71-72` | `cp:role:read` · **site** |
 * | - | `GET /v1/admin/permissions/roles/:slug/assignees` | `PermissionsProxyController.listRoleAssignees` | `cp:role:read` · **site** |
 * | ~~E46~~ | ~~`GET /v1/admin/permissions/groups/:id/members`~~ | ⛔ **MOVED to `api/groups.api.ts`** (R9 §4.1) | `cp:group:read` |
 * | E47 | `GET /v1/admin/permissions` (the catalog) | same `:56-57` | `cp:role:read` · **site** |
 * | E48 | `GET /v1/admin/sessions` | `sessions-proxy.controller.ts:23-24` | `cp:session:read` · **site** |
 * | E49 | `DELETE /v1/admin/users/:id/sessions` | `users-proxy.controller.ts:109-110` | `cp:session:revoke` · **site** |
 * | — | `GET /v1/admin/permissions/scim-clients` | `permissions-proxy.controller.ts:262-263` | `cp:scim-client:read` · **site** |
 *
 * Every one is on `/v1/admin` → site-admin-api (`httproute.yaml:213`).
 *
 * # ⛔ THE PROXY PATH IS NOT THE PRODUCER PATH, AND TWO OF THEM ACTUALLY DIFFER
 *
 * Verified at `PermissionsProxyService`'s own forwarding table rather than at the controller
 * decorators — that is the layer where the real hop is visible:
 *
 * | the SPA must call | forwards to cp-auth-api | |
 * |---|---|---|
 * | `/v1/admin/permissions/grants` | **`/v1/permissions/grants`** | ⚠️ differs (`:140`) |
 * | `/v1/admin/permissions/groups/:id/members` | **`/v1/admin/groups/:id/members`** | ⚠️ differs (`:267`) |
 * | the other eight | the same string | |
 *
 * ⛔ `GET /v1/admin/roles/:slug`, `/v1/admin/groups/:id/members` and `/v1/admin/scim-clients` are real
 * **cp-auth-api** routes and are **NOT reachable from the browser** (00-SPINE §2(a)): site-admin-api
 * owns `/v1/admin` at the gateway and declares no such route, so they answer a **JSON 404**.
 * "Simplifying" a proxy path to the producer's ships a screen that 404s on load — `A15` pins every
 * literal here and carries failing MSW handlers on those three unreachable paths.
 *
 * # ⚠️ EIGHT OF THE TEN PROXY METHODS ARE UNTYPED PASSTHROUGHS (`Promise<unknown>`)
 *
 * The third instance of this pattern in the programme (WS-1's E12/E6, WS-2's E21). ⇒ except for E42,
 * **each declaration below is the only place its shape is written down between the two services**, so
 * a producer change surfaces here as a runtime blank rather than a compile error. Each carries its
 * producer's `file:line`. **E42 alone has a real contract and is IMPORTED, never re-declared.**
 */
import type { AcceptedGrantPrincipalType, EffectiveUserPermissions, GrantResourceType } from '@zion/sdk-contracts';
import { api } from '../platform/http/client';

export type { EffectiveUserPermissions };
/**
 * ⛔ Re-exported so the four-arm exhaustive switch in `reach-model.ts` imports the union from ONE
 * place. ⚠️ Adding a member to it is **additive for producers and BREAKING for exhaustive consumers**
 * — by design: the contract's docblock calls the resulting TS2366 *"the SAFE failure here; the
 * dangerous one would have been a silent blank render."*
 */
export type { EffectivePermissionVia, EffectivePermissionEntry, CpPermission } from '@zion/sdk-contracts';

// ─────────────────────────────────────────────────────────────────────────────
// E41 — the operator list. ⛔ A PAGINATED ENVELOPE, NOT AN ARRAY.
// ─────────────────────────────────────────────────────────────────────────────

/** `UserView`, `cp-auth-api/src/modules/users/user.service.ts:32-44`. */
export interface UserView {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly role: string;
  readonly status: string;
  readonly source: string;
  readonly idpProvider: string | null;
  readonly externalId: string | null;
  readonly lastSyncedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * `PaginatedUsers`, `user.service.ts:70-76`.
 *
 * # ⛔ THIS IS AN ENVELOPE. `.map()` ON THE RESPONSE YIELDS `undefined`.
 *
 * WS-3 §4.1 calls E41 *"a list"* and §4.4 iterates *"every operator"*. It is neither: the producer
 * paginates with **`DEFAULT_PAGE_SIZE = 20`** (`user.service.ts:29`, applied at `:148`), and the
 * proxy's schema caps `pageSize` at **100** (`identity-proxy.schema.ts:67`).
 *
 * ⚠️ ⇒ **an unparameterised call resolves at most 20 operators**, and the reverse index built on it
 * would present that subset as *"who holds this permission"*. **The defect is invisible today** —
 * there are 9 live operators, so it fits under 20 and looks correct in review, in the fixture and in
 * production — and it would begin lying silently on the day the 21st operator is onboarded. Nothing
 * surfaces it. Correct-by-coincidence is worse than broken.
 *
 * ⭐ **`total` is what makes the truncation nameable**: the panel can say *"showing 20 of 47
 * operators"* rather than a vague "incomplete", which is the difference between a caveat and an
 * excuse. See {@link operatorPageBound}.
 */
export interface PaginatedUsers {
  readonly data: readonly UserView[];
  /** The true count matching the filter, independent of the page. ⭐ The truncation denominator. */
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly totalPages: number;
}

/** The proxy schema's ceiling (`identity-proxy.schema.ts:67`). ⛔ Requesting more is a 400. */
export const USERS_MAX_PAGE_SIZE = 100;

/** The producer's default when `pageSize` is omitted (`user.service.ts:29`). Recorded, never relied on. */
export const USERS_DEFAULT_PAGE_SIZE = 20;

export interface UsersQuery {
  readonly page?: number;
  readonly pageSize?: number;
  readonly search?: string;
}

export const USERS_PATH = '/v1/admin/users';

/**
 * ⛔ `pageSize` is **always sent**, at the schema maximum by default. Relying on the producer's
 * default would silently bound every consumer at 20 — see {@link PaginatedUsers}.
 */
export function usersPath(query: UsersQuery = {}): string {
  const params = new URLSearchParams();
  params.set('pageSize', String(query.pageSize ?? USERS_MAX_PAGE_SIZE));
  if (query.page !== undefined) params.set('page', String(query.page));
  if (query.search !== undefined) params.set('search', query.search);
  return `${USERS_PATH}?${params.toString()}`;
}

export function listUsers(query: UsersQuery = {}): Promise<PaginatedUsers> {
  return api.get<PaginatedUsers>(usersPath(query));
}

/**
 * How much of the operator population one page actually covered.
 *
 * ⛔ **`truncated` is the producer's OWN bound**, and it is a different cause from a failed fan-out
 * call or from our own cap — the three must never collapse into one "incomplete" message, because
 * their remedies differ: this one means *raise the page size or paginate*, a failure means *retry*.
 * (Lead ruling ACK-ws1-8.)
 */
export interface OperatorPageBound {
  readonly resolved: number;
  readonly total: number;
  readonly truncated: boolean;
}

export function operatorPageBound(page: PaginatedUsers): OperatorPageBound {
  return {
    resolved: page.data.length,
    total: page.total,
    // ⛔ `<`, not `!==`: a `total` BELOW the row count would mean the producer disagrees with itself,
    // and calling that "truncated" would report a missing-data problem for a counting bug.
    truncated: page.data.length < page.total,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// E42 — provenance. ⛔ The contract is IMPORTED.
// ─────────────────────────────────────────────────────────────────────────────

export function effectivePermissionsPath(userId: string): string {
  return `${USERS_PATH}/${encodeURIComponent(userId)}/effective-permissions`;
}

export function getEffectivePermissions(userId: string): Promise<EffectiveUserPermissions> {
  return api.get<EffectiveUserPermissions>(effectivePermissionsPath(userId));
}

// ─────────────────────────────────────────────────────────────────────────────
// E43 — resource grants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `ResourceGrantRow` — and ⚠️ **it is the raw Drizzle table row**, not a view type:
 * `cp-auth-api/src/db/schema/resource-grants.ts:101` is `typeof resourceGrants.$inferSelect`, returned
 * straight out of `resource-grants.controller.ts:80`.
 *
 * ⛔ **That makes the wire shape the DATABASE SCHEMA.** A renamed or added column changes the response
 * with no view layer in between and no contract to fail — the most exposed of the three untyped-hop
 * cases in this programme. Declared here off `resource-grants.ts:52-70`.
 *
 * `grantedAt`/`revokedAt` are `mode: 'date'` columns ⇒ ISO **strings** over JSON.
 */
export interface ResourceGrantRow {
  readonly id: string;
  readonly principalType: string;
  readonly principalId: string;
  readonly resourceType: string;
  readonly resourceId: string;
  readonly roleSlug: string;
  readonly grantedAt: string;
  readonly grantedBy: string | null;
  /** Non-null ⇒ the grant is revoked. */
  readonly revokedAt: string | null;
}

export const PERMISSIONS_PATH = '/v1/admin/permissions';

/** ⛔ `/v1/admin/permissions/grants` — the proxy forwards to `/v1/permissions/grants`, a DIFFERENT string. */
export const GRANTS_PATH = `${PERMISSIONS_PATH}/grants`;

export interface GrantsQuery {
  readonly principalType?: string;
  readonly principalId?: string;
  readonly resourceType?: string;
  readonly resourceId?: string;
}

export function grantsPath(query: GrantsQuery = {}): string {
  const params = new URLSearchParams();
  if (query.principalType !== undefined) params.set('principalType', query.principalType);
  if (query.principalId !== undefined) params.set('principalId', query.principalId);
  if (query.resourceType !== undefined) params.set('resourceType', query.resourceType);
  if (query.resourceId !== undefined) params.set('resourceId', query.resourceId);
  const q = params.toString();
  return q === '' ? GRANTS_PATH : `${GRANTS_PATH}?${q}`;
}

export function listGrants(query: GrantsQuery = {}): Promise<readonly ResourceGrantRow[]> {
  return api.get<readonly ResourceGrantRow[]>(grantsPath(query));
}

// ── The grants WRITE half — R9 WS-B N-e §4.3, the Grants screen ──────────────

/**
 * Body for `POST /v1/admin/permissions/grants` — `createGrantSchema`, `.strict()`
 * (`permissions-proxy.schema.ts`), which bounds `principalId`/`resourceId` at 256 and `roleSlug` at
 * 120 before the body crosses the service boundary.
 *
 * # ⛔⛔ `principalType` IS NOT INFERABLE FROM THE ID, AND THAT IS THIS SCREEN'S CENTRAL CONSTRAINT
 *
 * `resource_grants.principal_id` is bare `text` with **no foreign key**, so an operator id and a group
 * id are indistinguishable on sight. ⇒ **anything that decides or displays reach must read
 * `principalType`**, never guess from the id's shape. A grant on a GROUP reaches every operator in it;
 * rendering it as an operator grant understates the blast radius by the group's membership count.
 *
 * ⚠️ **`'user'` is a DEPRECATED WIRE ALIAS for `'operator'`, and it is accepted on purpose.** The proxy
 * validates against `ACCEPTED_GRANT_PRINCIPAL_TYPES` — the wire set including the alias — and
 * cp-auth-api normalises it before both the duplicate check and the INSERT
 * (`resource-grants.service.ts` `create()`). ⛔ Narrowing this union to the canonical pair would 400 a
 * body the deployment accepts, which is what a stale SPA bundle or a separately-installed `zion-ops`
 * sends mid-rollout. It stays readable so pre-R2 rows still render.
 *
 * ⛔ **`principalType` is `AcceptedGrantPrincipalType`, IMPORTED from `@zion/sdk-contracts`, not a
 * hand-written literal union (#1644 WS-6).** `'machine'` is now a canonical value the wire accepts
 * (`grant-principal.ts` §"Why 'machine' is in BOTH lists"), so a hand-written union naming only
 * `'operator' | 'group' | 'user'` would silently disagree with what the server does accept — the exact
 * drift this file's own duplication history (13 hand-copies before R2) exists to prevent. Deriving from
 * the shared constant means the next widening propagates here automatically instead of lying again.
 * ⚠️ **This does NOT offer `'machine'` as a choice** — see the `GrantFormDialog` principal choice, which
 * still lists only `operator` and `group`; machine-principal management is deferred (#1644, out of
 * scope for this screen). The type widening is a correctness fix for what the wire accepts, not a UI
 * change.
 */
export interface CreateGrantBody {
  readonly principalType: AcceptedGrantPrincipalType;
  readonly principalId: string;
  readonly resourceType: GrantResourceType;
  readonly resourceId: string;
  readonly roleSlug: string;
}

/** `cp:permission:grant` (`permissions-proxy.controller.ts` `@Post('grants')`). */
export function createGrant(body: CreateGrantBody): Promise<ResourceGrantRow> {
  return api.post<ResourceGrantRow>(GRANTS_PATH, body);
}

export function grantPath(grantId: string): string {
  return `${GRANTS_PATH}/${encodeURIComponent(grantId)}`;
}

/**
 * ⛔ **`cp:permission:grant`, and a REVOKE IS NOT A DELETE.** The producer stamps `revoked_at` and the
 * row survives — `listGrants` keeps returning it. ⇒ a screen that removed the row optimistically would
 * disagree with the very next read, and *"why did my revoke not appear"* is a support question a grants
 * screen that silently drops history cannot answer. **Re-read; do not splice.**
 *
 * `@HttpCode(NO_CONTENT)` on the proxy ⇒ genuinely **204**, no body.
 */
export function revokeGrant(grantId: string): Promise<void> {
  return api.delete<void>(grantPath(grantId));
}

// ─────────────────────────────────────────────────────────────────────────────
// E44 / E45 — roles
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A role definition. `cp-auth-api` `role_definitions`.
 *
 * ⚠️ `permissions` may contain a **wildcard** (`'*'` or `'prefix:*'`) — that is the whole subject of
 * the cutover screen. ⛔ Never expand it client-side; the server owns expansion.
 *
 * # ⛔ IT IS THE RAW DRIZZLE TABLE ROW, AND THIS DECLARATION WAS MISSING THREE FIELDS
 *
 * `roles.controller.ts:59` and `:88` both return **`RoleDefinitionRow`**, which is
 * `typeof roleDefinitions.$inferSelect` (`db/schema/role-definitions.ts:44`) — the table row itself,
 * with no view layer in between. So the wire shape is the DATABASE SCHEMA, the same exposure
 * {@link ResourceGrantRow} records, and the columns are exactly:
 * `slug · name · description · permissions · isBuiltin · createdAt · updatedAt · isResourceScoped`
 * (`role-definitions.ts:24-42`).
 *
 * ⚠️ **Measured because two consumers disagreed and neither was right.** This interface carried
 * `isResourceScoped` and omitted `isBuiltin`/`createdAt`/`updatedAt`; the old console's `Role`
 * (`site-admin/identity/src/roles/api.ts:16-24`) carries `isBuiltin` + the timestamps and omits
 * `isResourceScoped`. Both hops are untyped passthroughs (`Promise<unknown>` in
 * `permissions-proxy.service.ts`), so **nothing failed on either side** — the third instance of the
 * pattern this file's header names. Derived from the producer, not from either consumer.
 *
 * ⇒ The two booleans answer different questions and a surface needs both: `isBuiltin` is *"can this
 * role be edited or deleted"* (the catalogue's badge), `isResourceScoped` is *"may it be assigned
 * site-wide"* — `assertSiteWideAssignable` (`user.service.ts`, #1006) **refuses** the write for a
 * resource-scoped role, so offering one in a site-role picker is a door that 400s.
 *
 * `createdAt`/`updatedAt` are `mode: 'date'` columns ⇒ ISO **strings** over JSON.
 */
export interface RoleDefinition {
  readonly slug: string;
  readonly name: string;
  readonly description: string | null;
  readonly permissions: readonly string[];
  /** `true` ⇒ a platform-shipped role. ⛔ Not editable or deletable; the producer owns that rule. */
  readonly isBuiltin: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** `true` ⇒ grantable only ON a resource. ⛔ A site-wide assignment of one is refused (#1006). */
  readonly isResourceScoped: boolean;
}

export function rolePath(slug: string): string {
  return `${PERMISSIONS_PATH}/roles/${encodeURIComponent(slug)}`;
}

/** E45 — the roles assigned to one operator. ⛔ `roles/operators/:id`, not `operators/:id/roles`. */
export function operatorRolesPath(operatorId: string): string {
  return `${PERMISSIONS_PATH}/roles/operators/${encodeURIComponent(operatorId)}`;
}

export function getRole(slug: string): Promise<RoleDefinition> {
  return api.get<RoleDefinition>(rolePath(slug));
}

/** ⛔ The catalogue — `GET /v1/admin/permissions/roles` (`permissions-proxy.controller.ts:64-65`). */
export const ROLES_PATH = `${PERMISSIONS_PATH}/roles`;

/**
 * Every role definition. `cp:role:read`.
 *
 * ⛔ **A bare ARRAY of `RoleDefinitionRow`** (`roles.controller.ts:59`), not an envelope, and ⚠️ **not
 * paginated** — unlike E41. There is no `total`, so there is no truncation to name.
 */
export function listRoles(): Promise<readonly RoleDefinition[]> {
  return api.get<readonly RoleDefinition[]>(ROLES_PATH);
}

// ── Role ↔ operator assignment. ⛔ The ONLY routes that can REMOVE a role. ──

/**
 * ⛔ **THE TWO ROUTES `PATCH /users/:id/role` IS NOT.** That one is additive by design
 * (`user.service.ts:322-331`, *"the gate is a grant, not a replace"*): it inserts a `member_roles` row
 * and overwrites the cosmetic `operators.role` column, and **never removes anything**. A real role
 * change is a grant followed by a revoke, and the revoke lives here.
 *
 * Both are `cp:permission:grant` (`permissions-proxy.controller.ts:120-121`, `:128-129`).
 */
export function roleOperatorPath(slug: string, operatorId: string): string {
  return `${ROLES_PATH}/${encodeURIComponent(slug)}/operators/${encodeURIComponent(operatorId)}`;
}

/**
 * Grant a role to an operator.
 *
 * # ⛔⛔ 409 IS NOT A FAILURE — IT IS THE DESIRED STATE, ALREADY REACHED
 *
 * The producer answers **201** on success and **409 `'Operator already has the role'`*
 * (`roles.controller.ts:143-145`), and the proxy **preserves the 409** as a `ConflictException`
 * (`permissions-proxy.service.ts:477`) rather than flattening it. ⇒ a grant-then-revoke sequence that
 * treated 409 as an error would **abort on the one operator who already held the target role and leave
 * the old role in place** — a failed privilege change reported as a failure for a state that was
 * already correct.
 *
 * ⚠️ **This is why the sequence is not idempotent by accident**: `PATCH /users/:id/role` genuinely is
 * idempotent, and generalising that to this route is the plausible wrong move. See
 * `role-change.ts`'s `isAlreadyGranted`.
 *
 * ⚠️ Also note **403 does NOT survive the hop** — the proxy's error map handles 400/404/409 and turns
 * everything else, including an upstream 403, into a **503 `ServiceUnavailableException`**
 * (`permissions-proxy.service.ts:473-478`). site-admin-api's own gate refuses first for an
 * unauthorised caller, so this is not the common path, but a 503 here does not mean the service is down.
 */
export function grantRoleToOperator(slug: string, operatorId: string): Promise<unknown> {
  return api.post<unknown>(roleOperatorPath(slug, operatorId));
}

/**
 * Revoke a role from an operator. **204**, so genuinely no body
 * (`permissions-proxy.controller.ts:130`).
 *
 * # ⛔⛔ IT DELETES A `member_roles` ROW — AND A GROUP-INHERITED ROLE HAS NONE
 *
 * `siteRoles` on the cutover report is `member_roles` **∪** `group_roles`
 * (`cutover-impact.service.ts:249-273`). This route only removes the **direct** grant, so a role the
 * operator holds *through a group* answers **404 `'Operator does not have the role'`*
 * (`roles.controller.ts:158-159`) and the operator **keeps the role**.
 *
 * ⇒ ⛔ **A 404 here is neither success nor a transport failure**: it means *there was no direct grant to
 * remove*, and the role may still be in force. Treating it as success would report a privilege
 * reduction that did not happen — the worst available outcome for this control. The **re-read** is what
 * settles it, and `role-change.ts` names the case so the reader is told the remedy is in the group.
 */
export function revokeRoleFromOperator(slug: string, operatorId: string): Promise<void> {
  return api.delete<void>(roleOperatorPath(slug, operatorId));
}

/**
 * One row of E45: a `member_roles` assignment, as cp-auth-api's `RoleService.listOperatorRoles`
 * selects it. It names the role by slug and carries no definition.
 */
export interface OperatorRoleAssignment {
  readonly operatorId: string;
  readonly roleSlug: string;
  readonly grantedAt: string;
  readonly grantedBy: string | null;
}

/**
 * The direct site roles of one operator, as role definitions: E45 (the `member_roles` rows) joined
 * by slug with E43 (`listRoles`, the role list). Both routes need `cp:role:read`, so a 403 of either
 * is the 403 of the read.
 *
 * @throws the `ApiError` of either read; an `Error` that names the slug when E45 names a role that
 *   the role list lacks (the `member_roles` foreign key makes that a producer fault).
 */
export async function getOperatorRoles(operatorId: string): Promise<readonly RoleDefinition[]> {
  const [rows, catalog] = await Promise.all([
    api.get<readonly OperatorRoleAssignment[]>(operatorRolesPath(operatorId)),
    listRoles(),
  ]);
  const bySlug = new Map(catalog.map((role) => [role.slug, role]));
  return rows.map((row) => {
    const role = bySlug.get(row.roleSlug);
    if (role === undefined) throw new Error(`The role ${row.roleSlug} of this person is not in the role list.`);
    return role;
  });
}

// Role assignees: who holds one role, and through which path (access-members S2, WS-3).

/**
 * The answer of the role assignees read: who holds one role, and through which path.
 *
 * The producer is `GET /v1/admin/roles/:slug/assignees` in cp-auth-api, and its schema is
 * `roleAssigneesSchema` (`.strict()`). The browser calls the site-admin-api proxy path
 * {@link roleAssigneesPath}, which forwards the same body. The Held by tab of a role reads it.
 *
 * The answer carries ids and counts only: no operator name, no email, no group name. A consumer
 * resolves names through the reads it already has ({@link listUsers}, the groups reads).
 *
 * Fields:
 * - `slug`: the role slug from the route.
 * - `operators`: one entry per `member_roles` row of the role, so one per direct holder.
 * - `groups`: one entry per `group_roles` row of the role, with the member count of the group.
 * - `operatorsViaGroups`: the count of distinct members of those groups who hold no direct row
 *   for the role. An operator in two such groups counts once.
 * - `activeResourceGrants`: the count of unrevoked `resource_grants` rows that name the role.
 *
 * `grantedAt` is an ISO 8601 UTC string. `grantedBy` is the operator id that wrote the row, or
 * `null` when a service token or a seed wrote it.
 */
export interface RoleAssignees {
  readonly slug: string;
  readonly operators: readonly {
    readonly operatorId: string;
    readonly grantedAt: string;
    readonly grantedBy: string | null;
  }[];
  readonly groups: readonly {
    readonly groupId: string;
    readonly memberCount: number;
    readonly grantedAt: string;
    readonly grantedBy: string | null;
  }[];
  readonly operatorsViaGroups: number;
  readonly activeResourceGrants: number;
}

/**
 * The proxy path of the role assignees read: `/v1/admin/permissions/roles/<slug>/assignees`.
 * Built from {@link rolePath}, so the slug is URI-encoded once. The producer path
 * `/v1/admin/roles/<slug>/assignees` is unreachable from the browser: site-admin-api owns
 * `/v1/admin` at the gateway.
 */
export function roleAssigneesPath(slug: string): string {
  return `${rolePath(slug)}/assignees`;
}

/**
 * Read who holds one role. Permission {@link ROLE_READ_PERMISSION} (`cp:role:read`).
 *
 * - 200: the {@link RoleAssignees} answer. A role that nobody holds gives two empty arrays and two
 *   zero counts.
 * - 404: no role has the slug. The promise rejects with an `ApiError` whose `status` is 404.
 * - Any other failure rejects with an `ApiError` that carries the status the proxy sent.
 *
 * The read has no side effect on the server.
 */
export function getRoleAssignees(slug: string): Promise<RoleAssignees> {
  return api.get<RoleAssignees>(roleAssigneesPath(slug));
}

// ─────────────────────────────────────────────────────────────────────────────
// 06a **P-2 / G-5** — role WRITES. R4–R7.
//
// ⛔ **ADDED HERE, and the role READ surface is deliberately NOT moved out** (06a D-4). The E46
// precedent moved `cp:group:*` into its own module because a specific defect existed — a Groups
// screen could satisfy `permission-gate-parity` on `cp:operator:read` while owing `cp:group:read`.
// **That defect does not arise here**: `RolesPane`/`RolePane` are gated by the manifest on
// `cp:role:read`, which IS the producer's permission and IS declared by this module, so parity is
// already discharged on the right slug.
//
// ⚠️ The residual is that `permission-gate-parity` filters to values matching `/:read(-|$)/`, so it
// is **structurally blind** to `cp:role:create`/`:update`/`:delete`. That is closed by a per-control
// gate assertion in each pane suite (06a T-10.12), never by citing that gate.
//
// ⛔⛔ **FOUR WRITES, FOUR DIFFERENT PERMISSIONS.** R4 `cp:role:create` · R5 `cp:role:update` ·
// R6 `cp:role:delete` · **R7 `cp:permission:grant`** (NOT `cp:role:delete`). Collapsing any pair
// renders a control the server refuses.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Body for **R4** — the PROXY's `createRoleSchema` (`permissions-proxy.schema.ts:203-214`), `.strict()`.
 *
 * ⛔ **The proxy's bounds are the ones the browser is subject to, and they are NARROWER than the
 * producer's.** `role.dto.ts:65-72` has `description: z.string().optional()` with **no max** and
 * `permissions: z.array(z.string())` with **no per-item bound**; the proxy imposes 500 and 120. A
 * client mirror derived from the producer would accept a body the boundary rejects.
 */
export interface CreateRoleBody {
  /** 1..64 **and** {@link ROLE_SLUG_PATTERN}. */
  readonly slug: string;
  /** 1..128. */
  readonly name: string;
  /** ..500. */
  readonly description?: string;
  /** Each 1..120. ⛔ `[]` is legal — a role that grants nothing is a real, renderable answer. */
  readonly permissions: readonly string[];
}

/**
 * Body for **R5** (`permissions-proxy.schema.ts:221-227`), `.strict()`.
 *
 * ⛔ Every field optional, and that is load-bearing rather than convenience — see {@link updateRole}.
 * ⚠️ `description` is **NOT nullable**: there is no way to CLEAR it through this route.
 * ⛔ There is **no `slug` key** and `.strict()` refuses one — a slug is immutable.
 */
export interface UpdateRoleBody {
  readonly name?: string;
  readonly description?: string;
  readonly permissions?: readonly string[];
}

/**
 * ⛔ `/^[a-z][a-z0-9_-]{0,63}$/` — `cp-auth-api/src/modules/roles/dto/role.dto.ts:55`, **imported**
 * there by the proxy rather than re-typed. This copy is a LOOSE client mirror (06a D-12) and is
 * **not** the boundary: it may only ever err in the direction of asking the server.
 *
 * ⛔ **It is a SECURITY control, not formatting.** A role slug is unioned with the permission list at
 * authorization time, and `hasPermission` honours `*` and the `prefix:*` form — so a wildcard-SHAPED
 * SLUG would grant that wildcard from a `permissions` column that reads as harmless in an audit.
 */
export const ROLE_SLUG_PATTERN = /^[a-z][a-z0-9_-]{0,63}$/;

/**
 * ⛔ Names the **REASON**, never the regex. Copied from `ROLE_SLUG_PATTERN_MESSAGE` (`role.dto.ts:62`).
 *
 * ⚠️ A message reading *"must match /^[a-z][a-z0-9_-]{0,63}$/"* tells an operator what to type and
 * nothing about why a colon is refused, so the next person to widen the rule has no idea what they
 * would be widening.
 */
export const ROLE_SLUG_REASON =
  'Lowercase identifier: start with a letter, then letters, digits, underscore or hyphen (max 64). '
  + 'Colons and stars are rejected — a role slug is unioned with the permission list at authorization '
  + 'time, so a wildcard-shaped slug would grant that wildcard.';

/** R7's path. ⛔ Built FROM {@link rolePath}, never re-spelled. */
export function roleRevokeAllPath(slug: string): string {
  return `${rolePath(slug)}/revoke-all`;
}

/**
 * **R4** — create a role. **201** `RoleDefinition`.
 *
 * · **409** `Role <slug> already exists`.
 * · **400** on a slug-pattern refusal, carrying {@link ROLE_SLUG_REASON}'s server-side twin.
 */
export function createRole(body: CreateRoleBody): Promise<RoleDefinition> {
  return api.post<RoleDefinition>(ROLES_PATH, body);
}

/**
 * **R5** — update a role. **200** `RoleDefinition` · 404 · 400.
 *
 * # ⛔⛔ `permissions` IS A FULL REPLACEMENT, NOT A MERGE
 *
 * `RoleService.update` does `updates.permissions = [...dto.permissions]` (`role.service.ts:173`).
 * Combined with the fact that `GET /v1/admin/permissions` returns 88 strings with **zero wildcards**,
 * an editor whose submitted array is computed from catalog checkbox state alone **silently strips
 * `'*'` from `admin` the first time anyone renames it** — a total loss of the platform's most
 * consequential role, produced by editing a text field.
 *
 * ⇒ ⛔ **OMIT `permissions` ENTIRELY WHEN IT DID NOT CHANGE** (06a D-11). `panes/roles-model.ts`'s
 * `buildRolePatch` is the one implementation of that rule; an untouched set means the stored array
 * is never rewritten *at all*, rather than merely rewritten identically.
 *
 * ⚠️ **The wildcard-with-live-grants 400** (`role.service.ts:158-168`) names the offending
 * permissions and the live grant count. ⛔ The console surfaces it **verbatim** and does not
 * pre-check it — the live-grant count is a server fact this screen never reads.
 */
export function updateRole(slug: string, body: UpdateRoleBody): Promise<RoleDefinition> {
  return api.patch<RoleDefinition>(rolePath(slug), body);
}

/**
 * **R6** — delete a role. **204, no body** (`roles.controller.ts:117-125`) · 404 · **409 in-use**.
 *
 * # ⛔ THERE IS NO BUILT-IN SPECIAL CASE (06a D-15)
 *
 * `RoleService.delete`'s own docblock: *"D27: there is NO builtin special-case — a builtin role is
 * deleted exactly like a custom one (gated by `cp:role:delete` at the route + the in-use guard
 * here)"* (`role.service.ts:206-214`). ⇒ hiding the control for `isBuiltin` rows would be the
 * console asserting a rule the producer does not have. The self-lockout safety net is the **409
 * in-use guard** (SA-8), not a client-side convention.
 *
 * # ⛔⛔ R-77 / RS-1 F3 — `revokeRoleFromAllAssignees` IS NOT THE REMEDY FOR EVERY 409
 *
 * This docblock used to say it was. WS-4 RS-1 removed that claim from the producer because it is
 * **false for resource-grant assignees**: the 409 now counts `resource_grants` too, and revoke-all
 * clears `member_roles` + `group_roles` **only**.
 *
 * | what the 409 sentence names | the remedy |
 * |---|---|
 * | operator(s) / group(s) | {@link revokeRoleFromAllAssignees} (R7) |
 * | **resource grant(s)** | revoke the grant on each app / tenant / data plane. Revoke-all is **INERT** here: it returns `{0,0}` and changes nothing. |
 *
 * ⚠️ **Why this is security-visible and not a copy nit:** before RS-1 the FK violation surfaced as a
 * **503** (the proxy buckets non-400/404/409 into `ServiceUnavailable`), which the SPA auto-retries —
 * so a permanently-impossible delete was presented as a transient one. A screen that offers an inert
 * button under a sentence promising it works reproduces the operator-visible half of that defect.
 * ⭐ `revoked_at` is irrelevant: the FK is `onDelete('restrict')` and ignores it, so an already-revoked
 * grant still blocks the delete.
 *
 * ⛔ **Drive the offer off the server's own sentence, and do NOT regex it** — it is prose that has
 * already changed once this release.
 */
export function deleteRole(slug: string): Promise<void> {
  return api.delete<void>(rolePath(slug));
}

/** R7's result. ⛔ Two counts, and neither includes resource grants — see {@link deleteRole}. */
export interface RevokeFromAllResult {
  readonly revokedOperators: number;
  readonly revokedGroups: number;
}

/**
 * **R7** — revoke this role from every operator and every group. **200** · 404.
 *
 * ⛔ Gated on **`cp:permission:grant`** (`roles.controller.ts:127`), NOT on `cp:role:delete`. It is a
 * separately-audited act and ⛔ must never be a side effect of a delete the operator clicked once.
 */
export function revokeRoleFromAllAssignees(slug: string): Promise<RevokeFromAllResult> {
  return api.post<RevokeFromAllResult>(roleRevokeAllPath(slug));
}

export const ROLE_CREATE_PERMISSION = 'cp:role:create';
export const ROLE_UPDATE_PERMISSION = 'cp:role:update';
export const ROLE_DELETE_PERMISSION = 'cp:role:delete';
/*
 * ⛔ R7 reuses the EXISTING {@link PERMISSION_GRANT_PERMISSION} (`'cp:permission:grant'`, declared
 * with the grant family below). Not re-declared here: two literals for one slug in one module is the
 * drift this file's E46 note is about.
 */

// ─────────────────────────────────────────────────────────────────────────────
// E46 — group members ⇒ ⛔ MOVED OUT. See `api/groups.api.ts`.
// ─────────────────────────────────────────────────────────────────────────────

/*
 * ⛔⛔ **`GroupMemberView`, `groupMembersPath`, `listGroupMembers` AND `GROUP_READ_PERMISSION` NOW LIVE
 * IN `api/groups.api.ts` (R9 WS-B N-e §4.1). MOVED, NOT COPIED — do not re-add them here.**
 *
 * The reason is the GATE, not tidiness. `permission-gate-parity.test.ts` discharges a component's
 * obligation if it gates on **any one** permission its imported api module declares, and this module
 * declares six read grants. ⇒ a Groups screen importing `identity.api` would satisfy the check on
 * `cp:operator:read` and **prove nothing about `cp:group:read`**. A module declaring only the
 * `cp:group:*` family makes it sharp.
 *
 * ⚠️ ⇒ **Re-declaring any of them here would split `cp:group:read` across two modules and let the gate
 * be satisfied from the wrong one** — which is worse than the duplication, because it is silent.
 *
 * ⚠️ **And the "zero consumers" claim that authorised the move was measured over PRODUCTION code only
 * and was wrong.** Three of the four were imported by `panes/__tests__/access-api-paths.test.ts` — a
 * path-pinning suite, i.e. **precisely the file most likely to hold the one import of an otherwise
 * unadopted symbol.** ⭐ A search that excludes the place a thing would be is not a weak search, it is
 * an inverted one. Measure a no-consumers claim over all of `src`.
 */

// ─────────────────────────────────────────────────────────────────────────────
// E47 — the permission catalog
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⛔ A bare **array of permission strings** — `return [...ALL_CP_PERMISSIONS]`
 * (`cp-auth-api/src/modules/roles/permissions-catalog.controller.ts:27`). Not objects, not an envelope.
 */
export const PERMISSIONS_CATALOG_PATH = PERMISSIONS_PATH;

export function getPermissionsCatalog(): Promise<readonly string[]> {
  return api.get<readonly string[]>(PERMISSIONS_CATALOG_PATH);
}

// ─────────────────────────────────────────────────────────────────────────────
// E48 / E49 — sessions, and the one write WS-3 ships
// ─────────────────────────────────────────────────────────────────────────────

/** `AdminSessionView`, `cp-auth-api/src/modules/session/admin-sessions.service.ts:13-25`. */
export interface AdminSessionView {
  readonly id: string;
  readonly operatorId: string;
  readonly email: string;
  readonly displayName: string;
  readonly ipAddress: string | null;
  readonly userAgent: string | null;
  readonly sessionType: string;
  readonly label: string | null;
  readonly createdAt: string;
  readonly lastUsedAt: string | null;
  readonly expiresAt: string;
}

export const SESSIONS_PATH = '/v1/admin/sessions';

export function listSessions(): Promise<readonly AdminSessionView[]> {
  return api.get<readonly AdminSessionView[]>(SESSIONS_PATH);
}

/**
 * E49 — revoke **every** session for one operator. ⛔ `DELETE /v1/admin/users/:id/sessions` (plural,
 * on the USERS path), not `DELETE /v1/admin/sessions/:id` (which revokes one session by its own id).
 *
 * ⚠️ This is the **one write WS-3 ships**, because it is the remedy for the propagation gap the panel
 * exists to explain: a revoked grant does not take effect until the session's access token expires.
 * It is idempotent and non-destructive.
 *
 * # ⛔ 200 WITH A BODY — AND ITS NEIGHBOUR IS A 204. DO NOT COLLAPSE THEM.
 *
 * The producer is `@Delete(':id/sessions')` with `@ApiResponse({status: 200})` and
 * `return {message: 'Sessions revoked'}` (`users.controller.ts:146-156`) — **no `@HttpCode`**, so Nest's
 * DELETE default of 200 stands, and the proxy (`users-proxy.controller.ts:108-114`) adds none either.
 * Meanwhile {@link revokeSession} (E80, ONE session) really is 204, because
 * `sessions-proxy.controller.ts:30-31` carries `@HttpCode(204)`.
 *
 * ⇒ ⛔ two adjacent revoke routes with two different response protocols. This was typed `Promise<void>`
 * because I generalised from the sibling whose `@HttpCode(204)` I had already recorded four functions
 * below — the fact was in front of me and I extended it to the wrong route. `void` is a lie about a body
 * that exists (`parseBody` hands the object back regardless, `client.ts:131-146`), and the same
 * assumption made the dev fixture answer a 204 the deployment never sends.
 */
export function userSessionsPath(userId: string): string {
  return `${USERS_PATH}/${encodeURIComponent(userId)}/sessions`;
}

export function revokeUserSessions(userId: string): Promise<MessageResponse> {
  return api.delete<MessageResponse>(userSessionsPath(userId));
}

// ─────────────────────────────────────────────────────────────────────────────
// SCIM clients — the externally-managed signal
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⛔ `/v1/admin/permissions/scim-clients` (`permissions-proxy.controller.ts:262`), **not**
 * `/v1/admin/scim-clients` — that is the unreachable cp-auth-api path.
 *
 * ⚠️ `cp:scim-client:read` is deliberately **NOT** in the Reach entry's `anyOf`, so this panel
 * degrades independently: a principal without it sees the rest of the screen and **nothing** here —
 * not an error.
 */
export interface ScimClientView {
  readonly id: string;
  readonly name: string;
  readonly status: string;
}

export const SCIM_CLIENTS_PATH = `${PERMISSIONS_PATH}/scim-clients`;

export function listScimClients(): Promise<readonly ScimClientView[]> {
  return api.get<readonly ScimClientView[]>(SCIM_CLIENTS_PATH);
}

// ── Permissions, named once so a gate and a `ForbiddenState`'s copy cannot drift ──

export const OPERATOR_READ_PERMISSION = 'cp:operator:read';
export const ROLE_READ_PERMISSION = 'cp:role:read';
/* ⛔ `GROUP_READ_PERMISSION` MOVED to `api/groups.api.ts` — see the E46 note above. Not a copy. */
export const GRANT_READ_PERMISSION = 'cp:permission-grant:read';
export const SESSION_READ_PERMISSION = 'cp:session:read';
export const SESSION_REVOKE_PERMISSION = 'cp:session:revoke';
export const SCIM_CLIENT_READ_PERMISSION = 'cp:scim-client:read';

// ═════════════════════════════════════════════════════════════════════════════
// R7 WS-5 §3 — **People** (screen 60). E70–E80 + E76.
//
// ⛔ ONE LIST AUTHORITY, AND IT IS `/v1/admin/users`. site-admin-api exposes TWO operator lists on
// the browser-reachable prefix — this one (a PROXY to cp-auth-api's forked users surface,
// `users-proxy.controller.ts:45-110`) and its OWN `/v1/admin/operators`
// (`operator-admin.controller.ts:37-80`). Only `users` carries status, role and the session verbs, so
// screen 60 lists from it and calls `operators` for **one thing only**: passkeys (E76). ⛔ Do not
// render two lists and ⛔ do not merge a row across the two — they are different surfaces with
// different verbs, and a merged row makes it ambiguous which service a write went to. `A7` is the
// mechanical guard: a failing MSW handler on `GET /v1/admin/operators`, with E76 as the control
// proving the ban is path-specific rather than blanket.
//
// ⭐ **READ "People", SEND `users`.** The proxy's own docblock (`users-proxy.controller.ts:31-36`):
// *"The 'Operators' human label is kept in the UI (FR-CP.2); the route vocabulary mirrors the
// upstream `users` mount."* Same R7-DEC-6 discipline as release-channel/`ring`. ⛔ Never rename a path.
// ═════════════════════════════════════════════════════════════════════════════

// ── E70 — operator detail ───────────────────────────────────────────────────

/** `UserSessionSummary`, `cp-auth-api/src/modules/users/user.service.ts:46-55`. */
export interface UserSessionSummary {
  readonly id: string;
  readonly ipAddress: string | null;
  readonly userAgent: string | null;
  readonly sessionType: string;
  readonly label: string | null;
  readonly createdAt: string;
  readonly lastUsedAt: string | null;
  readonly expiresAt: string;
}

/**
 * `UserPasskeySummary`, `user.service.ts:58-62`.
 *
 * ⚠️ **A DIFFERENT SHAPE FROM E76's {@link PasskeySummary}** — `deviceName: string | null` here,
 * `label: string` there, because site-admin-api's own reader applies the `?? 'Passkey'` fallback
 * (`operator-admin.service.ts:139-143`) and cp-auth-api's does not. ⛔ Two names for two shapes: one
 * interface used for both would make the null reachable through a field typed non-null.
 */
export interface UserPasskeySummary {
  readonly id: string;
  readonly deviceName: string | null;
  readonly createdAt: string;
}

/**
 * `UserDetailView`, `user.service.ts:65-68`.
 *
 * ⭐ **It carries `sessions` AND `passkeys` inline.** WS-5 §1's claim that
 * `/v1/admin/operators/:id/passkeys` is *"the ONLY producer of passkey facts"* is therefore not
 * accurate — this route produces them too, for one operator, in one call. Reported to the lead;
 * §4's table keeps E76 as the expand-time source and `A7`'s control depends on that choice, so the
 * plan is implemented as written and this note exists so the next reader does not re-derive it.
 */
export interface UserDetailView extends UserView {
  readonly sessions: readonly UserSessionSummary[];
  readonly passkeys: readonly UserPasskeySummary[];
}

export function userPath(userId: string): string {
  return `${USERS_PATH}/${encodeURIComponent(userId)}`;
}

export function getUser(userId: string): Promise<UserDetailView> {
  return api.get<UserDetailView>(userPath(userId));
}

// ── The two write-door enums ────────────────────────────────────────────────

/**
 * `USER_ROLES`, the copy of the site-admin-api identity proxy schema. These are the values of the
 * legacy single `role` column, which the users controller calls cosmetic. The snapshot builder
 * reads `member_roles` and `role_definitions` at mint.
 *
 * The three site roles of 2026-10-20-cp-permissioning (D1): `platform_admin`, `fleet_operator` and
 * `app_developer`. The folded `platform_operator` and `platform_viewer` keep their holders until
 * migration 0050 maps them in stage 2, and their rows until 0051 deletes them in stage 3. The write
 * doors offer them no more. `user-roles-copies-parity.spec.ts`
 * in `@zion/cp-parity-tests` compares this copy with the cp-auth-api definition by set equality.
 */
export const USER_ROLES = ['platform_admin', 'fleet_operator', 'app_developer'] as const;
export type UserRole = (typeof USER_ROLES)[number];

/**
 * `USER_STATUSES`, `identity-proxy.schema.ts:32`.
 *
 * ⛔ **A WRITE-DOOR CONSTRAINT ONLY.** `UserView.status` is a bare `string` on the read path
 * (`user.service.ts:37`), so a row can carry a value outside this list — a legacy row, a value added
 * upstream, a SCIM import. ⇒ the stage derivation in `people-model.ts` is closed with an explicit
 * **unknown** arm rendering the raw value, and ⛔ never `default: 'active'`, which would paint an
 * unrecognised state green. WS-5 §4.1 / **A3**.
 */
export const USER_STATUSES = ['active', 'disabled', 'invited'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

// ── E71 / E72 / E73 / E74 / E75 — the user writes ───────────────────────────

/** Body for E71, `createUserSchema` (`identity-proxy.schema.ts:35-43`). `.strict()` upstream. */
export interface CreateUserBody {
  readonly email: string;
  readonly displayName: string;
  readonly role: UserRole;
  /** Optional; omitted ⇒ the operator has no password and signs in by another factor. */
  readonly password?: string;
}

export function createUser(body: CreateUserBody): Promise<UserView> {
  return api.post<UserView>(USERS_PATH, body);
}

/** Body for E72, `updateUserSchema` (`identity-proxy.schema.ts:46-52`). Both fields optional. */
export interface UpdateUserBody {
  readonly displayName?: string;
  readonly status?: UserStatus;
}

export function updateUser(userId: string, body: UpdateUserBody): Promise<UserView> {
  return api.patch<UserView>(userPath(userId), body);
}

/**
 * The `{message}` envelope four of these writes answer with.
 *
 * ⛔ **NOT `void`, and not 204.** `users.controller.ts:105-143` returns HTTP **200** with
 * `{message: 'User deactivated'}` / `'…reactivated'`. Typing them `void` would be a quiet lie about a
 * body that exists, and `parseBody` would still hand the object back (`client.ts:131-146`).
 */
export interface MessageResponse {
  readonly message: string;
}

/**
 * E73 — ⛔ **`DELETE`, and the producer DEACTIVATES.** `users-proxy.controller.ts:85-86` and
 * `operator-admin.controller.ts:66-67` both name it *"Remove (deactivate)"*; the row survives with
 * `status` flipped. ⇒ every label and every confirmation on this screen says **Deactivate**, ⛔ never
 * "Delete" — a word that promises an erasure the platform does not perform. **A12** pins it.
 */
export function deactivateUser(userId: string): Promise<MessageResponse> {
  return api.delete<MessageResponse>(userPath(userId));
}

export function userRolePath(userId: string): string {
  return `${userPath(userId)}/role`;
}

/**
 * E74 — ⛔ gated on **`cp:permission:grant`** (`users-proxy.controller.ts:93-94`), NOT on any
 * `cp:operator:*`. A separately-holdable permission: changing who can do what is a different act from
 * administering an account, and collapsing the two gates would show a role editor to someone the
 * server will 403.
 */
export function updateUserRole(userId: string, role: UserRole): Promise<MessageResponse> {
  return api.patch<MessageResponse>(userRolePath(userId), { role });
}

export function userReactivatePath(userId: string): string {
  return `${userPath(userId)}/reactivate`;
}

/**
 * E75 — ⛔ **`cp:operator:update` gates BOTH this and E72** (`users-proxy.controller.ts:77-78`,
 * `:101-102`): one permission, two verbs. Collapse the *check*, never the controls.
 */
export function reactivateUser(userId: string): Promise<MessageResponse> {
  return api.post<MessageResponse>(userReactivatePath(userId));
}

// ── E76 — passkeys, and the ONLY reason this screen touches `/v1/admin/operators` ──

/**
 * ⛔ **THE OTHER LIST'S PREFIX, deliberately unexported as a list source.** There is no
 * `listOperators` here and there must not be: §1 rules `/v1/admin/users` the list authority, and an
 * exported list function on this prefix is the one edit that would make the two-list trap easy to
 * reintroduce. Only the per-operator passkeys child hangs off it.
 */
const OPERATORS_PATH = '/v1/admin/operators';

/**
 * `PasskeySummaryDto`, `site-admin-api/src/modules/operator-admin/operator-admin.service.ts:42-46`.
 *
 * ⚠️ `label` is **already defaulted** to `'Passkey'` when the operator never named the device
 * (`:141`) ⇒ it is `string`, never null. Contrast {@link UserPasskeySummary}, E70's shape, which is
 * the raw `deviceName` and IS nullable.
 */
export interface PasskeySummary {
  readonly id: string;
  readonly label: string;
  readonly createdAt: string;
}

export function operatorPasskeysPath(operatorId: string): string {
  return `${OPERATORS_PATH}/${encodeURIComponent(operatorId)}/passkeys`;
}

/**
 * ⚠️ **ONE CALL PER PERSON.** ⛔ Never fanned out across the list — with N rows rendered this is N
 * requests for a column nobody has asked to see. It fires on **row expand** and only then.
 * **A8** counts the requests: zero with five rows rendered, exactly one after expanding one.
 */
export function listOperatorPasskeys(operatorId: string): Promise<readonly PasskeySummary[]> {
  return api.get<readonly PasskeySummary[]>(operatorPasskeysPath(operatorId));
}

// ── E77 / E78 / E79 — invitations ───────────────────────────────────────────

/**
 * `InvitationView`, `cp-auth-api/src/modules/invitations/invitation.service.ts:50-59`.
 *
 * ⛔ **NO `acceptUrl`, and its absence is a SECURITY FIX (#1155), not an omission.** The link used to
 * ride in every row of this response behind `cp:operator:read` — a delegable permission — so any
 * reader could lift a pending token and redeem it at the `@Public()` accept route. It now lives on
 * `GET /v1/admin/invitations/:id/accept-url` behind `cp:operator:invite`
 * (`invitations-admin.controller.ts`). site-admin-api proxies it behind the same permission, and
 * cp-auth-api writes an audit row for each read of the link.
 *
 * ⇒ ⛔ **no "copy invite link" affordance on any row, and no function here that could call it.**
 * The console keeps the link out by design. **A9** pins both halves with a failing MSW handler on
 * that path.
 */
export interface InvitationView {
  readonly id: string;
  readonly email: string;
  readonly role: string;
  readonly status: string;
  readonly invitedBy: string | null;
  readonly expiresAt: string;
  readonly acceptedAt: string | null;
  readonly createdAt: string;
}

/**
 * `INVITATION_STATUSES`, `identity-proxy.schema.ts:75`. ⚠️ **Expiry is NOT one of them** — it is
 * computed from `expires_at`, so a row can be `pending` and already expired.
 */
export const INVITATION_STATUSES = ['pending', 'accepted', 'revoked'] as const;
export type InvitationStatus = (typeof INVITATION_STATUSES)[number];

export const INVITATIONS_PATH = '/v1/admin/invitations';

export interface InvitationsQuery {
  readonly status?: InvitationStatus;
}

export function invitationsPath(query: InvitationsQuery = {}): string {
  // ⛔ `.strict()` upstream (`identity-proxy.schema.ts:87-91`) ⇒ an unknown key is a 400, and an
  // explicit `?status=undefined` string is an unknown VALUE. Omitted means omitted.
  return query.status === undefined
    ? INVITATIONS_PATH
    : `${INVITATIONS_PATH}?status=${encodeURIComponent(query.status)}`;
}

/** E77 — ⛔ a bare ARRAY, newest first (`invitations-admin.controller.ts:58-66`). No envelope. */
export function listInvitations(query: InvitationsQuery = {}): Promise<readonly InvitationView[]> {
  return api.get<readonly InvitationView[]>(invitationsPath(query));
}

/**
 * `InvitationCreatedView`, `invitation.service.ts:61-75` — ⛔ **the ONLY response shape in this family
 * that carries `acceptUrl`**, and a SEPARATE interface rather than an optional field on
 * {@link InvitationView} for exactly that reason: *"an optional `acceptUrl?` would let a future
 * `list()` populate it again and still typecheck"*.
 *
 * ⚠️ **There is no email send.** The CP fork stripped `EmailService`: *"LINK-COPY UX (AD §4): `create`
 * returns the accept URL instead of mailing it (no mail module on cp-auth-api). The admin delivers
 * the link."* (`invitation.service.ts:80-83`.) ⇒ the link exists **exactly once**, in this response,
 * and cannot be re-read afterwards from the browser. What screen 60 does with it is a decision with
 * an owner, raised with the lead; ⛔ do not add a re-reveal affordance in either direction.
 */
export interface InvitationCreatedView extends InvitationView {
  readonly acceptUrl: string;
}

/** Body for E78, `createInvitationSchema` (`identity-proxy.schema.ts:89`). `.strict()` upstream. */
export interface CreateInvitationBody {
  readonly email: string;
  /**
   * ⛔ **REQUIRED, and there is deliberately NO default anywhere in the chain (#1359).**
   *
   * ⚠️ This was `role?`, and the comment here said *"omitted ⇒ the accept flow grants the producer's
   * own default site role."* **That default has been deleted and the sentence described a mechanism
   * that no longer exists** — the more dangerous half being that it told a reader of this file the
   * key was safe to leave out.
   *
   * ⛔ **Omitting it is now a 400, not a default.** Both producers require it, verified first-hand:
   * `cp-auth-api/.../dto/invitation.dto.ts:46-48` is `z.enum(USER_ROLES)` with **no `.optional()`**,
   * and the `site-admin-api` proxy schema (`identity-proxy.schema.ts:89`) matches — its own spec
   * asserts `safeParse({ email }).success === false`.
   *
   * ⭐ **Why the producer removed the default, so nobody restores one here:** it was
   * `platform_viewer`, which is site-wide and holds `cp:app:runtime:read` plus all three
   * `cp:observability:read-*`. ⇒ **One invitation with the `role` key simply left out** minted a
   * principal authorized for every app on every data plane, container stdout included. *Not sending a
   * field is not an act anyone reviews; naming a role is.* A narrow default would need a slug that is
   * not fleet-wide, and `USER_ROLES` has none — that is a permission-model decision, not a defaulting
   * one.
   */
  readonly role: UserRole;
}

/** E78 — `cp:operator:invite`. ⛔ Shares that permission with E79; see {@link revokeInvitation}. */
export function createInvitation(body: CreateInvitationBody): Promise<InvitationCreatedView> {
  return api.post<InvitationCreatedView>(INVITATIONS_PATH, body);
}

export function invitationPath(invitationId: string): string {
  return `${INVITATIONS_PATH}/${encodeURIComponent(invitationId)}`;
}

/**
 * E79 — ⛔ **`cp:operator:invite`, the SAME permission as E78** (`invitations-proxy.controller.ts:39-40`,
 * `:47-48`). ⇒ Invite and Revoke-invitation **hide together**. That is correct — revoking a credential
 * you were entitled to issue is the same authority — and it is stated here because splitting the two
 * gates is the plausible-looking wrong edit. **A10** asserts both arms.
 */
export function revokeInvitation(invitationId: string): Promise<MessageResponse> {
  return api.delete<MessageResponse>(invitationPath(invitationId));
}

// ── E80 — revoke ONE session ────────────────────────────────────────────────

export function sessionPath(sessionId: string): string {
  return `${SESSIONS_PATH}/${encodeURIComponent(sessionId)}`;
}

/**
 * E80 — ⛔ `DELETE /v1/admin/sessions/:id` revokes **one** session; {@link revokeUserSessions} (E49)
 * revokes **every** session for an operator. Two routes, two blast radii — ⛔ never one call with a
 * flag.
 *
 * ✅ **SHIPPED on the operator detail screen** (`PersonPane`'s Sessions card), lead ruling ACK-access-4.
 *
 * ⚠️ **The deferral note that stood here has been DELETED, not amended.** It read *"WS-5 ships no
 * control for this (§7.4): a per-session control needs the session list rendered inside the expanded
 * person, which is a second expand level, and that is deferred pending the lead's sign-off."* Its
 * precondition was **met, not waived** — the detail screen renders the session list at the top level,
 * so no second expand level is required — and the sign-off it named was given. ⛔ A deferral that has
 * been satisfied but still reads as open is the stale-artifact class this programme keeps tripping on:
 * the next reader would decline to build a control that already exists, or build a second one.
 *
 * ⛔ **Click-confirm, NOT a typed challenge** (same ruling). Revoking a session is disruptive and
 * **recoverable** — the person signs in again. Typed challenges are reserved for the irreversible; using
 * one here would train operators to type through the gate that guards credential removal.
 *
 * `@HttpCode(204)` on the proxy (`sessions-proxy.controller.ts:30-31`) ⇒ genuinely no body.
 */
export function revokeSession(sessionId: string): Promise<void> {
  return api.delete<void>(sessionPath(sessionId));
}

// ── The four write permissions, named once ──────────────────────────────────

export const OPERATOR_INVITE_PERMISSION = 'cp:operator:invite';
export const OPERATOR_UPDATE_PERMISSION = 'cp:operator:update';
export const OPERATOR_REMOVE_PERMISSION = 'cp:operator:remove';
/** ⛔ `cp:permission:grant`, not a `cp:operator:*`. See {@link updateUserRole}. */
export const PERMISSION_GRANT_PERMISSION = 'cp:permission:grant';
